import * as THREE from 'three';
import HeroModule from '$src/js/modules/hero/HeroModule';
import { HeroState } from '$src/js/states/HeroState.svelte';

interface BlendUniforms {
  uTexB: { value: THREE.Texture | null };
  uMix: { value: number };
}

type BlendableMaterial = THREE.MeshStandardMaterial & {
  _blendUniforms?: BlendUniforms;
};

const FADE_SPEED = 0.04;

export class TextureService {
  private skinMeshes: THREE.Mesh[] = [];
  private skinShaderMat: BlendableMaterial | null = null;
  private skinTextures: [THREE.Texture | null, THREE.Texture | null] = [null, null];
  private skinFadeTarget = 0;
  private skinFadeProgress = 0;

  /**
   * Collects all mesh objects that should have the skin texture applied.
   * It registers loader tasks for the two skin textures and builds the shader when both are loaded.
   * @param model The Three.js model to traverse.
   */
  readonly collectSkinMeshes = (model: THREE.Object3D): void => {
    const shirtNode = model.getObjectByName('Visma_Shirt_Web');
    const target = shirtNode ?? model;
    const loaderService = HeroModule.getInstance().loader;
    const texLoader = new THREE.TextureLoader();

    // Register loader tasks for skin textures
    loaderService.registerTask('textureSkinA', 2);
    loaderService.registerTask('textureSkinB', 2);

    // Collect skin meshes
    target.traverse((child) => {
      if (child instanceof THREE.Mesh && child.name !== 'Visma_Shirt_Web_Inside') {
        this.skinMeshes.push(child as THREE.Mesh);
      }
    });

    const onLoad = (tex: THREE.Texture, idx: 0 | 1): void => {
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.flipY = false;
      tex.anisotropy = HeroModule.getInstance().scene.getMaxAnisotropy();
      this.skinTextures[idx] = tex;

      // Complete loader task
      loaderService.completeTask(idx === 0 ? 'textureSkinA' : 'textureSkinB');

      // Build shader when both textures are loaded
      if (this.skinTextures[0] && this.skinTextures[1]) {
        this.buildSkinShader();
      }
    };

    // Load skin textures
    texLoader.load('https://yellow-b-global.b-cdn.net/production/the-architect/model/Image_0.png?v=1', (t) => onLoad(t, 0));
    texLoader.load('https://yellow-b-global.b-cdn.net/production/the-architect/model/Image_1.png?v=1', (t) => onLoad(t, 1));
  };

  /**
   * Builds the skin shader material with blend uniforms.
   */
  private readonly buildSkinShader = (): void => {
    const baseMesh = this.skinMeshes[0];
    const base: THREE.MeshStandardMaterial =
      baseMesh.material instanceof THREE.MeshStandardMaterial
        ? baseMesh.material.clone()
        : new THREE.MeshStandardMaterial();

    base.map = this.skinTextures[0];
    base.needsUpdate = true;

    const blendUniforms: BlendUniforms = {
      uTexB: { value: this.skinTextures[1] },
      uMix: { value: 0.0 },
    };

    base.onBeforeCompile = (shader) => {
      shader.uniforms.uTexB = blendUniforms.uTexB;
      shader.uniforms.uMix = blendUniforms.uMix;

      shader.fragmentShader =
        /* glsl */ `
        uniform sampler2D uTexB;
        uniform float uMix;
      ` + shader.fragmentShader;

      shader.fragmentShader = shader.fragmentShader.replace(
        '#include <map_fragment>',
        /* glsl */ `
        #include <map_fragment>
        {
          vec4 texB = texture2D(uTexB, vMapUv);
          diffuseColor = mix(diffuseColor, texB, uMix);
        }
        `,
      );
    };

    (base as BlendableMaterial)._blendUniforms = blendUniforms;

    this.skinShaderMat = base as BlendableMaterial;

    for (const mesh of this.skinMeshes) {
      mesh.material = base;
    }

    HeroModule.getInstance().scene.markDirty();
  };

  /**
   * Sets the active skin index and updates the hero state.
   * @param index The skin index to set (0 or 1).
   */
  readonly setSkin = (index: 0 | 1): void => {
    this.skinFadeTarget = index;
    HeroState.activeSkin = index;
  };

  /**
   * Updates the crossfade animation for the skin texture.
   */
  readonly updateCrossfade = (): void => {
    if (!this.skinShaderMat || Math.abs(this.skinFadeProgress - this.skinFadeTarget) <= 0.001) {
      return;
    }

    this.skinFadeProgress += (this.skinFadeTarget - this.skinFadeProgress) * FADE_SPEED * 3;

    if (this.skinShaderMat._blendUniforms) {
      this.skinShaderMat._blendUniforms.uMix.value = this.skinFadeProgress;
    }

    HeroModule.getInstance().scene.markDirty();
  };
}
